import { format, isSameDay } from 'date-fns';
import { toZonedTime, format as formatTz } from 'date-fns-tz';

export function formatDate(date, { timeZone = null, timeZoneName = '', locale = 'en-US' } = {}) {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(timeZone ? { timeZone, ...(timeZoneName ? { timeZoneName } : {}) } : {}),
  };

  try {
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  } catch (error) {
    try {
      if (timeZone) {
        return format(new Date(date), 'MMM d, yyyy z');
      }
    } catch (error) {
      try {
        return format(new Date(date), 'MMM d, yyyy');
      } catch (error) {
        return date;
      }
    }
  }
}

export function formatTime(date, { timeZone = null, timeZoneName = '', locale = 'en-US' } = {}) {
  const options = {
    hour: 'numeric',
    minute: 'numeric',
    ...(timeZone ? { timeZone, ...(timeZoneName ? { timeZoneName } : {}) } : {}),
  };

  try {
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  } catch (error) {
    try {
      if (timeZone) {
        return format(new Date(date), 'h:mm a z');
      }
    } catch (error) {
      return format(new Date(date), 'h:mm a');
    }
  }
}

export function formatDateTime(
  date,
  { timeZone = null, timeZoneName = '', locale = 'en-US' } = {},
) {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    ...(timeZone ? { timeZone, ...(timeZoneName ? { timeZoneName } : {}) } : {}),
  };

  try {
    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  } catch (error) {
    try {
      if (timeZone) {
        return format(new Date(date), 'MMM d, yyyy, h:mm a z');
      }
    } catch (error) {
      try {
        return format(new Date(date), 'MMM d, yyyy, h:mm a');
      } catch (error) {
        return date;
      }
    }
  }
}

export function formatEventDate({ startDate, endDate, timezone, locale = 'en-US' }) {
  if (startDate && !endDate) {
    return [formatDateTime(startDate, { timeZone: timezone, timeZoneName: 'short', locale })];
  }

  if (endDate && !startDate) {
    return [formatDateTime(endDate, { timeZone: timezone, timeZoneName: 'short', locale })];
  }

  const start = toZonedTime(new Date(startDate), timezone);
  const end = endDate ? toZonedTime(new Date(endDate), timezone) : null;

  // Same day event
  if (end && isSameDay(start, end)) {
    return [
      `${formatDateTime(startDate, { timeZone: timezone, locale })} -
      ${formatTime(endDate, { timeZone: timezone, timeZoneName: 'short', locale })}`,
    ];
  }

  // Multi-day event
  if (end) {
    return [
      formatDateTime(startDate, { timeZone: timezone, timeZoneName: 'short', locale }),
      formatDateTime(endDate, { timeZone: timezone, timeZoneName: 'short', locale }),
    ];
  }

  // Single date and time event
  return [formatDateTime(startDate, { timeZone: timezone, timeZoneName: 'short', locale })];
}

// Get timezone abbreviation (e.g., "PST", "EST", "UTC")
function getTimezoneAbbreviation(timeZone, date = new Date()) {
  if (!timeZone) return '';

  try {
    const abbreviation = formatTz(date, 'zzz', { timeZone });
    return abbreviation;
  } catch (_) {
    return '';
  }
}

// Format datetime with timezone abbreviation
export function formatDateTimeWithTimezone(
  date,
  { timeZone = null, locale = 'en-US', showTimezone = true } = {},
) {
  if (!timeZone) {
    return formatDateTime(date, { locale });
  }

  const formattedDate = formatDateTime(date, { timeZone, locale });

  if (!showTimezone) {
    return formattedDate;
  }

  const abbreviation = getTimezoneAbbreviation(timeZone, new Date(date));
  return abbreviation ? `${formattedDate} ${abbreviation}` : formattedDate;
}
